import {
  useLayoutEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction
} from 'react';
import type {
  MonthlySearchIndex,
  MonthlySearchResult
} from '../utils/buildMonthlySearchIndex';

type SearchResult = MonthlySearchResult;

function getSearchResultKey(result: SearchResult): string {
  return result.releasePhaseId
    ? `${result.id}-${result.parentId || 'noparent'}-${result.releasePhaseId}-${
        result.showOnTimeline
      }`
    : result.parentId
    ? `${result.id}-${result.parentId}`
    : result.id;
}

interface RefreshSearchActions {
  clearHighlightedIssues: () => { type: string };
  setSearchFilterActive: (active: boolean) => {
    type: string;
    payload: boolean;
  };
  setSearchFilteredItems: (items: string[]) => {
    type: string;
    payload: string[];
  };
  clearSearchFilter: () => { type: string };
}

interface UseRefreshSearchOnViewChangeParams {
  timelineViewMode: string;
  searchIndex: MonthlySearchIndex;
  searchValue: string;
  generateSearchResults: (value: string) => SearchResult[];
  searchFilterActive: boolean;
  selectedResults: Set<string>;
  setSearchResults: Dispatch<SetStateAction<SearchResult[]>>;
  setLastSearchResults: Dispatch<SetStateAction<SearchResult[]>>;
  setSelectedResults: Dispatch<SetStateAction<Set<string>>>;
  updateHighlighting: (
    results: SearchResult[],
    selectionOverride?: Set<string>
  ) => void;
  dispatch: (action: { type: string }) => void;
  actions: RefreshSearchActions;
  searchTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
}

function refreshActiveSearch({
  searchValue,
  generateSearchResults,
  searchFilterActive,
  selectedResults,
  setSearchResults,
  setLastSearchResults,
  setSelectedResults,
  updateHighlighting,
  dispatch,
  actions
}: Omit<
  UseRefreshSearchOnViewChangeParams,
  'timelineViewMode' | 'searchIndex' | 'searchTimeoutRef'
>) {
  const results = generateSearchResults(searchValue);
  setSearchResults(results);
  setLastSearchResults(results);

  const prunedSelections = new Set(
    Array.from(selectedResults).filter(key =>
      results.some(r => getSearchResultKey(r) === key)
    )
  );

  if (prunedSelections.size !== selectedResults.size) {
    setSelectedResults(prunedSelections);
  }

  dispatch(actions.clearHighlightedIssues());
  if (results.length > 0) {
    updateHighlighting(results, prunedSelections);
  }

  if (searchFilterActive) {
    if (prunedSelections.size > 0) {
      const selectedIds = Array.from(prunedSelections).map(selectedKey => {
        const match = results.find(r => getSearchResultKey(r) === selectedKey);
        if (!match) return selectedKey;
        if (match.type !== 'child' && match.releasePhaseId) {
          return `${match.id}-${match.releasePhaseId}`;
        }
        return match.id;
      });
      dispatch(
        actions.setSearchFilteredItems(Array.from(new Set(selectedIds)))
      );
    } else if (results.length > 0) {
      dispatch(actions.setSearchFilterActive(true));
      dispatch(actions.setSearchFilteredItems(results.map(r => r.id)));
    } else {
      dispatch(actions.clearSearchFilter());
    }
  }
}

/**
 * Re-runs search when the active view context changes: quarterly/monthly toggle,
 * monthly date range (search index rebuild), or release data updates.
 */
export function useRefreshSearchOnViewChange({
  timelineViewMode,
  searchIndex,
  searchValue,
  generateSearchResults,
  searchFilterActive,
  selectedResults,
  setSearchResults,
  setLastSearchResults,
  setSelectedResults,
  updateHighlighting,
  dispatch,
  actions,
  searchTimeoutRef
}: UseRefreshSearchOnViewChangeParams) {
  const prevTimelineViewModeRef = useRef(timelineViewMode);
  const prevSearchIndexRef = useRef(searchIndex);

  useLayoutEffect(() => {
    const viewModeChanged =
      prevTimelineViewModeRef.current !== timelineViewMode;
    const searchIndexChanged = prevSearchIndexRef.current !== searchIndex;

    prevTimelineViewModeRef.current = timelineViewMode;
    prevSearchIndexRef.current = searchIndex;

    if (!viewModeChanged && !searchIndexChanged) {
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    if (searchValue.length < 3) {
      return;
    }

    refreshActiveSearch({
      searchValue,
      generateSearchResults,
      searchFilterActive,
      selectedResults,
      setSearchResults,
      setLastSearchResults,
      setSelectedResults,
      updateHighlighting,
      dispatch,
      actions
    });
  }, [
    timelineViewMode,
    searchIndex,
    searchValue,
    generateSearchResults,
    searchFilterActive,
    selectedResults,
    setSearchResults,
    setLastSearchResults,
    setSelectedResults,
    updateHighlighting,
    dispatch,
    actions,
    searchTimeoutRef
  ]);
}
